/**
 * Timezone Utility Functions
 * Handles timezone conversions and date formatting for uniform date/time data
 * All functions return dates in IST (Indian Standard Time: UTC+5:30)
 */

import moment from 'moment';

// Common date formats used throughout the application
export const DATE_FORMATS = {
  DISPLAY_DATE: 'DD/MM/YYYY',
  DISPLAY_DATETIME: 'DD/MM/YYYY, h:mm:ss A',
  DISPLAY_TIME: 'h:mm:ss A',
  DISPLAY_DATE_SHORT: 'DD/MM/YY',
  API_DATE: 'YYYY-MM-DD',
  API_DATETIME: 'YYYY-MM-DD HH:mm:ss',
  ISO_DATETIME: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  RECEIPT_DATE: 'DD/MM/YYYY, h:mm A',
  TABLE_DATE: 'DD/MM/YYYY',
  TABLE_DATETIME: 'DD/MM/YYYY HH:mm'
} as const;

/**
 * Convert a date to IST (Indian Standard Time)
 * @param date - Date string or Date object
 * @param format - Output format (default: 'DD/MM/YYYY, h:mm:ss A')
 * @returns Formatted date string in IST
 */
export const convertToIST = (
  date: string | Date,
  format: string = DATE_FORMATS.DISPLAY_DATETIME
): string => {
  return moment(date).utcOffset('+05:30').format(format);
};

/**
 * Convert a date to IST with custom format
 * @param date - Date string or Date object
 * @param format - Output format
 * @returns Formatted date string in IST
 */
export const formatIST = (date: string | Date, format: string): string => {
  return moment(date).utcOffset('+05:30').format(format);
};

/**
 * Get current date in IST
 * @param format - Output format (default: 'DD/MM/YYYY, h:mm:ss A')
 * @returns Current date string in IST
 */
export const getCurrentIST = (format: string = DATE_FORMATS.DISPLAY_DATETIME): string => {
  return moment().utcOffset('+05:30').format(format);
};

/**
 * Get current date as ISO string in IST
 * @returns Current date as ISO string in IST
 */
export const getCurrentISTISO = (): string => {
  return moment().utcOffset('+05:30').toISOString();
};

/**
 * Parse a date string and convert to IST
 * @param dateString - Date string to parse
 * @param inputFormat - Input format (default: 'MM/DD/YYYY, h:mm:ss A')
 * @param outputFormat - Output format (default: 'DD/MM/YYYY, h:mm:ss A')
 * @returns Formatted date string in IST
 */
export const parseAndConvertToIST = (
  dateString: string,
  inputFormat: string = 'MM/DD/YYYY, h:mm:ss A',
  outputFormat: string = DATE_FORMATS.DISPLAY_DATETIME
): string => {
  return moment(dateString, inputFormat).utcOffset('+05:30').format(outputFormat);
};

/**
 * Format date for display in tables
 * @param date - Date string or Date object
 * @returns Formatted date string for table display
 */
export const formatForTable = (date: string | Date): string => {
  return convertToIST(date, DATE_FORMATS.TABLE_DATE);
};

/**
 * Format datetime for display in tables
 * @param date - Date string or Date object
 * @returns Formatted datetime string for table display
 */
export const formatDateTimeForTable = (date: string | Date): string => {
  return convertToIST(date, DATE_FORMATS.TABLE_DATETIME);
};

/**
 * Format date for receipts
 * @param date - Date string or Date object
 * @returns Formatted date string for receipts
 */
export const formatForReceipt = (date: string | Date): string => {
  return convertToIST(date, DATE_FORMATS.RECEIPT_DATE);
};

/**
 * Format date for API calls (ISO format)
 * @param date - Date string or Date object
 * @returns ISO formatted date string
 */
export const formatForAPI = (date: string | Date): string => {
  return moment(date).utcOffset('+05:30').format(DATE_FORMATS.API_DATETIME);
};

/**
 * Check if a date is today
 * @param date - Date string or Date object
 * @returns True if the date is today
 */
export const isToday = (date: string | Date): boolean => {
  const inputDate = moment(date).utcOffset('+05:30');
  const today = moment().utcOffset('+05:30');
  return inputDate.isSame(today, 'day');
};

/**
 * Check if a date is yesterday
 * @param date - Date string or Date object
 * @returns True if the date is yesterday
 */
export const isYesterday = (date: string | Date): boolean => {
  const inputDate = moment(date).utcOffset('+05:30');
  const yesterday = moment().utcOffset('+05:30').subtract(1, 'day');
  return inputDate.isSame(yesterday, 'day');
};

/**
 * Get relative time (e.g., "2 hours ago", "in 3 days")
 * @param date - Date string or Date object
 * @returns Relative time string
 */
export const getRelativeTime = (date: string | Date): string => {
  return moment(date).utcOffset('+05:30').fromNow();
};

/**
 * Get days difference between two dates
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Number of days difference
 */
export const getDaysDifference = (date1: string | Date, date2: string | Date): number => {
  const d1 = moment(date1).utcOffset('+05:30');
  const d2 = moment(date2).utcOffset('+05:30');
  return d1.diff(d2, 'days');
};

/**
 * Add days to a date
 * @param date - Base date
 * @param days - Number of days to add
 * @returns New date with days added
 */
export const addDays = (date: string | Date, days: number): Date => {
  return moment(date).utcOffset('+05:30').add(days, 'days').toDate();
};

/**
 * Subtract days from a date
 * @param date - Base date
 * @param days - Number of days to subtract
 * @returns New date with days subtracted
 */
export const subtractDays = (date: string | Date, days: number): Date => {
  return moment(date).utcOffset('+05:30').subtract(days, 'days').toDate();
};

/**
 * Get start of day in IST
 * @param date - Date string or Date object
 * @returns Start of day as Date object
 */
export const getStartOfDay = (date: string | Date): Date => {
  return moment(date).utcOffset('+05:30').startOf('day').toDate();
};

/**
 * Get end of day in IST
 * @param date - Date string or Date object
 * @returns End of day as Date object
 */
export const getEndOfDay = (date: string | Date): Date => {
  return moment(date).utcOffset('+05:30').endOf('day').toDate();
};